import React from 'react';

type PremiumErrorTone = 'warning' | 'error' | 'neutral';

interface PremiumErrorSurfaceProps {
	title: string;
	description: React.ReactNode;
	icon: React.ReactNode;
	tone?: PremiumErrorTone;
	autoFocusTitle?: boolean;
	testId?: string;
	children?: React.ReactNode;
}

const TONE_TO_COLOR: Record<PremiumErrorTone, string> = {
	warning: 'var(--warning-color, var(--warning-text, #ffb800))',
	error: 'var(--error-color, var(--error-text, #ff4757))',
	neutral: 'var(--main-text-secondary, #64748b)',
};

export function PremiumErrorSurface({
	title,
	description,
	icon,
	tone = 'warning',
	autoFocusTitle = true,
	testId,
	children,
}: PremiumErrorSurfaceProps) {
	const [visible, setVisible] = React.useState(false);
	const titleRef = React.useRef<HTMLHeadingElement | null>(null);

	React.useEffect(() => {
		const id = window.requestAnimationFrame(() => setVisible(true));
		return () => window.cancelAnimationFrame(id);
	}, []);

	React.useEffect(() => {
		if (!autoFocusTitle) {
			return;
		}
		const id = window.setTimeout(() => titleRef.current?.focus(), 10);
		return () => window.clearTimeout(id);
	}, [autoFocusTitle]);

	return (
		<div className="premium-error" data-testid={testId}>
			<div
				className={`premium-error__card ${visible ? 'is-visible' : ''}`}
				style={{ ['--premium-error-tone' as string]: TONE_TO_COLOR[tone] }}
				role="region"
				aria-label={title}
			>
				<div className="premium-error__orb premium-error__orb--ring" aria-hidden="true" />

				<div className="premium-error__icon-wrap" aria-hidden="true">
					<span className="premium-error__icon-halo" />
					<div className="premium-error__icon">{icon}</div>
				</div>

				<h1 ref={titleRef} className="premium-error__title" tabIndex={-1}>{title}</h1>
				<div className="premium-error__description">{description}</div>
				{children}
			</div>
		</div>
	);
}

